'use client';
/* 
  Because of the legacy-URL-structure we had to implement some rewrites in the proxy.ts (middleware).
  We had to implement this custom param-handling hook because of that.
*/

import { useCallback, useMemo, useSyncExternalStore } from 'react';

const subscribers = new Set<() => void>();

const notifySubscribers = () => {
  queueMicrotask(() => subscribers.forEach((cb) => cb()));
};

if (typeof window !== 'undefined') {
  const originalPushState = history.pushState.bind(history);
  const originalReplaceState = history.replaceState.bind(history);

  history.pushState = (...args) => {
    originalPushState(...args);
    notifySubscribers();
  };

  history.replaceState = (...args) => {
    originalReplaceState(...args);
    notifySubscribers();
  };

  window.addEventListener('popstate', notifySubscribers);
}

const getSnapshot = () => window.location.search;
const getServerSnapshot = () => '';

export const useUrlSearchParams = () => {
  const search = useSyncExternalStore(
    (cb) => {
      subscribers.add(cb);

      return () => subscribers.delete(cb);
    },
    getSnapshot,
    getServerSnapshot,
  );

  const params = useMemo(() => new URLSearchParams(search), [search]);

  const setParams = useCallback((updater: (params: URLSearchParams) => URLSearchParams) => {
    const next = updater(new URLSearchParams(window.location.search));
    const qs = next.toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    history.pushState(null, '', url);
  }, []);

  return [params, setParams] as const;
};
